/**
 * ChatView Component
 * Container principale della conversazione
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { MessageSquare } from 'lucide-react';
import { ChatHeader } from './ChatHeader';
import { ChatNotesPanel } from './ChatNotesPanel';
import { MessageSearchBar } from './MessageSearchBar';
import { MessageList } from '../messages/MessageList';
import { MessageInput } from '../input/MessageInput';
import { TaskFromSuggestionModal } from '../input/TaskFromSuggestionModal';
import { ImageLightbox } from '../../team-chat/ImageLightbox';
import { blobCache } from '../messages/MediaProxy';
import { GroupInfoPanel } from '../group/GroupInfoPanel';
import { ChatAgentPanel } from './ChatAgentPanel';
import { UnifiedRightPanel } from './UnifiedRightPanel';
import { SalesTaskFromSuggestionModal } from './SalesTaskFromSuggestionModal';
import { ConnectionBanner } from './ConnectionBanner';
import { RecontactStrip } from './RecontactStrip';
import { toggleDebugPanel } from './ChatDebugPanel';
import { ForwardModal } from '../messages/ForwardModal';
import { ForwardToTeamChatModal } from '../messages/ForwardToTeamChatModal';
import { MessageSelectionProvider } from '../context/MessageSelectionContext';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { useWhatsAppAgentStore } from '@/lib/stores/whatsappAgentStore';
import { useWhatsAppChatMetaStore } from '@/lib/stores/whatsappChatMetaStore';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import type { Message } from '@/lib/types/whatsappChat';
import type { AgentRequest } from '@/lib/types/whatsappAgent';

interface ChatViewProps {
  onBack?: () => void;
  compactMode?: boolean;
}

function EmptyState() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
      <div className="w-16 h-16 rounded-full bg-[rgba(55,53,47,0.08)] dark:bg-white/10 flex items-center justify-center mb-4">
        <MessageSquare className="w-8 h-8 text-[#787774]" />
      </div>
      <h3 className="text-lg font-medium text-[#37352F] dark:text-white mb-2">
        Seleziona una chat
      </h3>
      <p className="text-sm text-[#787774] max-w-sm">
        Scegli una conversazione dalla lista per iniziare a chattare
      </p>
    </div>
  );
}

export function ChatView({ onBack, compactMode }: ChatViewProps) {
  const [showRightPanelToggled, setShowRightPanelToggled] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [showChatAgent, setShowChatAgent] = useState(false);
  // Debug panel state gestito globalmente in ChatDebugPanel (toggleDebugPanel)
  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchIndex, setSearchIndex] = useState(0);
  const [taskSuggestionRequest, setTaskSuggestionRequest] = useState<AgentRequest | null>(null);
  // Da un suggerimento si crea di norma un Task commerciale sul Lead/Cliente;
  // 'project' = l'utente ha scelto il task di progetto (modale esistente).
  const [taskTarget, setTaskTarget] = useState<'sales' | 'project'>('sales');
  const [forwardingMessageId, setForwardingMessageId] = useState<string | null>(null);
  const [forwardToTeamChatMessages, setForwardToTeamChatMessages] = useState<Message[] | null>(null);

  // Larghezza della vista: in compatto il pannello destro (380px) si affianca solo se
  // alla chat restano almeno 420px, altrimenti si apre sopra la chat (collaudo 28/09:
  // affiancato le lasciava ~100px e bolle e pulsanti uscivano dal bordo sinistro).
  const [rootWidth, setRootWidth] = useState(0);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const rootRef = useCallback((el: HTMLDivElement | null) => {
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => setRootWidth(entry.contentRect.width));
    observer.observe(el);
    resizeObserverRef.current = observer;
  }, []);
  const panelOverChat = rootWidth > 0 && rootWidth < 380 + 420;

  // P2: Selettori individuali per evitare re-render inutili
  const selectedChatId = useWhatsAppChatStore(s => s.selectedChatId);
  const chats = useWhatsAppChatStore(s => s.chats);
  const selectedChatData = useWhatsAppChatStore(s => s.selectedChatData);
  const presenceMap = useWhatsAppChatStore(s => s.presenceMap);
  const setReplyingTo = useWhatsAppChatStore(s => s.setReplyingTo);
  const replyingTo = useWhatsAppChatStore(s => s.replyingTo);
  const sendReadReceipt = useWhatsAppChatStore(s => s.sendReadReceipt);
  const wahaConfig = useWhatsAppChatStore(s => s.wahaConfig);
  const messages = useWhatsAppChatStore(s => s.messages);

  const chatSuggestions = useWhatsAppAgentStore(s => s.chatSuggestions);
  const assignments = useWhatsAppAgentStore(s => s.assignments);
  const fetchChatSuggestions = useWhatsAppAgentStore(s => s.fetchChatSuggestions);

  const chatNotes = useWhatsAppChatMetaStore(s => s.chatNotes);
  const fetchOrgMembers = useWhatsAppChatMetaStore(s => s.fetchOrgMembers);
  const fetchNotes = useWhatsAppChatMetaStore(s => s.fetchNotes);

  const { user } = useAuth();

  const setChatUnreadCount = useWhatsAppChatStore(s => s.setChatUnreadCount);
  const retryPendingMessage = useWhatsAppChatStore(s => s.retryPendingMessage);

  // Fallback su selectedChatData se la chat non è nel chats array
  const selectedChat = chats.find((c) => c.id === selectedChatId) || selectedChatData;
  // Presence: cerca per selectedChatId (@c.us), fallback per senderJid @lid degli inbound
  // (WAHA NOWEB invia presence con @lid che non matcha il chatId @c.us della sidebar)
  const presence = useMemo(() => {
    if (!selectedChatId) return undefined;
    const direct = presenceMap.get(selectedChatId);
    if (direct) return direct;
    // Fallback @lid: cerca il senderJid del primo messaggio inbound
    const inboundSenderJid = messages.find(m => m.direction === 'inbound')?.senderJid;
    if (inboundSenderJid) return presenceMap.get(inboundSenderJid);
    return undefined;
  }, [selectedChatId, presenceMap, messages]);

  // Notes count for badge
  const notesCount = selectedChatId ? (chatNotes.get(selectedChatId) || []).length : 0;

  // Search: filtra messaggi per query (client-side)
  const searchMatches = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return messages
      .filter((m) => m.content?.toLowerCase().includes(q))
      .map((m) => m.id);
  }, [messages, searchQuery]);

  // Array immagini per lightbox (usa blobCache se disponibile, altrimenti URL originale)
  const lightboxImages = useMemo(() => {
    return messages
      .filter((m) => m.type === 'image' && m.mediaUrl)
      .map((m) => ({
        url: blobCache.get(m.mediaUrl!) || m.mediaUrl!,
        name: m.mediaFilename || undefined,
        originalUrl: m.mediaUrl!,
      }));
  }, [messages]);

  // Ref per accedere a lightboxImages nel callback senza ricrearlo
  const lightboxImagesRef = useRef(lightboxImages);
  lightboxImagesRef.current = lightboxImages;

  // Carica org members al montaggio
  useEffect(() => {
    if (wahaConfig?.organizationId) {
      fetchOrgMembers(wahaConfig.organizationId);
    }
  }, [wahaConfig?.organizationId, fetchOrgMembers]);

  // Carica note quando cambia la chat selezionata
  useEffect(() => {
    if (selectedChatId && wahaConfig?.organizationId) {
      fetchNotes(selectedChatId, wahaConfig.organizationId);
    }
    // Reset UI panels su cambio chat
    setShowNotesPanel(false);
    setShowSearchBar(false);
    setSearchQuery('');
    setSearchIndex(0);
  }, [selectedChatId, wahaConfig?.organizationId, fetchNotes]);

  // Marca la chat come letta quando viene selezionata (store + WAHA + DB)
  useEffect(() => {
    if (selectedChatId) {
      setChatUnreadCount(selectedChatId, 0);
      sendReadReceipt(selectedChatId);
      // Persisti nel DB (fire-and-forget)
      if (user?.id && wahaConfig?.configId && wahaConfig?.organizationId) {
        supabase.rpc('mark_whatsapp_chat_as_read', {
          p_user_id: user.id,
          p_config_id: wahaConfig.configId,
          p_organization_id: wahaConfig.organizationId,
          p_chat_jid: selectedChatId,
          p_message_id: null,
          p_timestamp: new Date().toISOString(),
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChatId]);

  // Fetch agent suggestions whenever the chat changes
  useEffect(() => {
    if (selectedChatId && wahaConfig?.organizationId) {
      fetchChatSuggestions(selectedChatId, wahaConfig.organizationId);
    }
  }, [selectedChatId, wahaConfig?.organizationId, fetchChatSuggestions]);

  const handleReply = useCallback((message: Message) => {
    setReplyingTo(message);
  }, [setReplyingTo]);

  const handleMediaClick = useCallback((mediaUrl: string) => {
    // Cerca l'indice nell'array immagini lightbox
    const images = lightboxImagesRef.current;
    const idx = images.findIndex((img) => img.originalUrl === mediaUrl || img.url === mediaUrl);
    if (idx >= 0) {
      // Aggiorna le URL con le ultime dalla blobCache (potrebbero essere state caricate dopo il memo)
      setLightboxIndex(idx);
    } else {
      // Fallback: video o altro media non in lightbox, apri come singola immagine
      setLightboxIndex(null);
      // Per video/doc, non aprire lightbox (non supportato)
    }
  }, []);

  const handleForwardMessage = useCallback((messageId: string) => {
    setForwardingMessageId(messageId);
  }, []);

  const handleForwardToTeamChat = useCallback((message: Message) => {
    setForwardToTeamChatMessages([message]);
  }, []);

  const handleToggleGroupInfo = useCallback(() => {
    if (selectedChat?.isGroup) {
      setShowGroupInfo((prev) => !prev);
    }
  }, [selectedChat?.isGroup]);

  const handleToggleChatAgent = useCallback(() => {
    setShowChatAgent((prev) => !prev);
  }, []);

  const handleCreateTaskFromSuggestion = useCallback((request: AgentRequest) => {
    setTaskTarget('sales');
    setTaskSuggestionRequest(request);
  }, []);

  const handleSearchPrev = useCallback(() => {
    if (searchMatches.length === 0) return;
    setSearchIndex((i) => (i - 1 + searchMatches.length) % searchMatches.length);
  }, [searchMatches.length]);

  const handleSearchNext = useCallback(() => {
    if (searchMatches.length === 0) return;
    setSearchIndex((i) => (i + 1) % searchMatches.length);
  }, [searchMatches.length]);

  // Get assignment for current chat (for project info)
  const currentAssignment = selectedChatId
    ? assignments.find(a => a.chat_jid === selectedChatId)
    : null;

  if (!selectedChatId || !selectedChat) {
    return <EmptyState />;
  }

  return (
    <MessageSelectionProvider>
      <div ref={rootRef} className="relative flex h-full overflow-hidden bg-[#FBFBFA] dark:bg-gray-950">
        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
          {/* Header */}
          <ChatHeader
            chat={selectedChat}
            presence={presence}
            onBack={onBack}
            onInfoClick={handleToggleGroupInfo}
            onAgentClick={handleToggleChatAgent}
            onNotesClick={() => setShowNotesPanel((v) => !v)}
            notesCount={notesCount}
            onSearchClick={() => { setShowSearchBar((v) => !v); setSearchQuery(''); }}
            onDebugClick={import.meta.env.DEV ? toggleDebugPanel : undefined}
            onForwardToTeamChat={(msgs) => setForwardToTeamChatMessages(msgs)}
            onPanelToggle={compactMode ? () => setShowRightPanelToggled(v => !v) : undefined}
            isPanelOpen={compactMode ? showRightPanelToggled : undefined}
            compact={compactMode}
          />

          {/* Numero attivo scollegato: i messaggi non partono */}
          <ConnectionBanner configId={wahaConfig?.configId} />

          {/* Search bar */}
          {showSearchBar && (
            <MessageSearchBar
              query={searchQuery}
              onChange={(q) => { setSearchQuery(q); setSearchIndex(0); }}
              totalResults={searchMatches.length}
              currentIndex={searchIndex}
              onPrev={handleSearchPrev}
              onNext={handleSearchNext}
              onClose={() => { setShowSearchBar(false); setSearchQuery(''); }}
            />
          )}

          {/* Messages */}
          <MessageList
            chatId={selectedChatId}
            isGroup={selectedChat.isGroup}
            presence={presence}
            onReply={handleReply}
            onMediaClick={handleMediaClick}
            onForward={handleForwardMessage}
            onForwardToTeamChat={handleForwardToTeamChat}
            onRetry={retryPendingMessage}
            chatProfilePicUrl={selectedChat.profilePicUrl}
            chatName={selectedChat.name}
            searchQuery={searchQuery}
            searchHighlightIndex={searchMatches.length > 0 ? searchIndex : -1}
            searchMatchIds={searchMatches}
          />

          {/* Promemoria "Ricontatta" scaduto o cliente che ha risposto */}
          <RecontactStrip chat={selectedChat} organizationId={wahaConfig?.organizationId} />

          {/* Input */}
          <MessageInput
            chatId={selectedChatId}
            chatName={selectedChat.name}
            replyingTo={replyingTo}
            onCancelReply={() => setReplyingTo(null)}
            suggestions={chatSuggestions}
            isAgentActive={!!currentAssignment && currentAssignment.agent_response_mode !== 'off'}
            onCreateTaskFromSuggestion={handleCreateTaskFromSuggestion}
            onOpenAgentPanel={() => setShowChatAgent(true)}
          />

          {/* Notes Panel (slide-in dall'interno della main area) */}
          {selectedChatId && wahaConfig?.organizationId && (
            <ChatNotesPanel
              chatJid={selectedChatId}
              organizationId={wahaConfig.organizationId}
              isOpen={showNotesPanel}
              onClose={() => setShowNotesPanel(false)}
            />
          )}
        </div>

        {/* Group Info Panel */}
        {selectedChat?.isGroup && (
          <GroupInfoPanel
            groupId={selectedChatId}
            groupName={selectedChat.name}
            isOpen={showGroupInfo}
            onClose={() => setShowGroupInfo(false)}
          />
        )}

        {/* Chat Agent Config Panel (settings) */}
        <ChatAgentPanel
          chatId={selectedChatId}
          chatName={selectedChat.name}
          isGroup={selectedChat.isGroup}
          isOpen={showChatAgent}
          onClose={() => setShowChatAgent(false)}
        />

        {/* Pannello destro unificato CRM + Agent AI */}
        {compactMode ? (
          showRightPanelToggled && (
            <div className={panelOverChat ? 'absolute inset-y-0 right-0 z-30 flex max-w-full shadow-2xl' : 'flex h-full flex-shrink-0'}>
              <UnifiedRightPanel
                chatId={selectedChatId}
                chatName={selectedChat.name}
                isOpen={true}
                onClose={() => setShowRightPanelToggled(false)}
                onCreateTask={handleCreateTaskFromSuggestion}
                onOpenNotes={() => setShowNotesPanel(true)}
                chatNotesCount={notesCount}
                rememberCollapse={false}
              />
            </div>
          )
        ) : (
          <UnifiedRightPanel
            chatId={selectedChatId}
            chatName={selectedChat.name}
            isOpen={true}
            onClose={() => {}}
            onCreateTask={handleCreateTaskFromSuggestion}
            onOpenNotes={() => setShowNotesPanel(true)}
            chatNotesCount={notesCount}
          />
        )}

        {/* Forward Modal (WA → WA) */}
        {forwardingMessageId && (
          <ForwardModal
            messageId={forwardingMessageId}
            onClose={() => setForwardingMessageId(null)}
          />
        )}

        {/* Forward to Team Chat Modal (WA → Team Chat) */}
        {forwardToTeamChatMessages && selectedChat && (
          <ForwardToTeamChatModal
            messages={forwardToTeamChatMessages}
            chatName={selectedChat.name}
            chatId={selectedChatId}
            onClose={() => setForwardToTeamChatMessages(null)}
          />
        )}

        {/* Image Lightbox con zoom, rotazione, gallery */}
        {lightboxIndex !== null && lightboxImages.length > 0 && (
          <ImageLightbox
            images={lightboxImages}
            initialIndex={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
          />
        )}

        {/* Task commerciale da suggerimento (predefinito, non serve un progetto) */}
        {taskSuggestionRequest && taskTarget === 'sales' && (
          <SalesTaskFromSuggestionModal
            request={taskSuggestionRequest}
            clientId={currentAssignment?.client?.id || null}
            clientName={currentAssignment?.client?.business_name || null}
            organizationId={wahaConfig?.organizationId || null}
            projectName={currentAssignment?.project?.name || null}
            onClose={() => setTaskSuggestionRequest(null)}
            onChooseProject={(currentAssignment?.project_id || taskSuggestionRequest.project_id) ? () => setTaskTarget('project') : undefined}
          />
        )}

        {/* Task di progetto da suggerimento (scelto dall'utente) */}
        {taskSuggestionRequest && taskTarget === 'project' && (
          <TaskFromSuggestionModal
            isOpen={true}
            onClose={() => setTaskSuggestionRequest(null)}
            request={taskSuggestionRequest}
            projectId={currentAssignment?.project_id || taskSuggestionRequest.project_id || null}
            projectName={currentAssignment?.project?.name || null}
          />
        )}
      </div>
    </MessageSelectionProvider>
  );
}
